import { useQuery } from '@tanstack/react-query'
import apiClient from '@/api/client'

/**
 * useKitchenFlowEnabled tells the UI whether this tenant runs the caterer
 * "kitchen flow" (backend env `FEATURE_KITCHEN_FLOW=1`, surfaced as the
 * server-injected `kitchen_flow` key on GET /settings — env-authoritative,
 * never stored in app_settings).
 *
 * Every kitchen-flow surface (barcodes, scanning, purchase units, lots, prep
 * items, the Kitchen Manager role, the Production page, the Store "Requests"
 * and "Locations" tabs) renders only when this is true. A missing key (old
 * backend) or a failed fetch reads as off — the safe direction: the ~15 live
 * tenants must see nothing new.
 *
 * Shares the `['settings','all']` cache with the rest of the app, so it adds
 * no network call.
 */
export function useKitchenFlowEnabled(): boolean {
  return useKitchenFlowGate().enabled
}

export interface KitchenFlowGate {
  /** true only when settings have loaded AND this tenant runs the kitchen flow. */
  enabled: boolean
  /**
   * true while the answer is not settled yet — the first fetch, OR a refetch
   * that could still turn a cached "off" into "on". A route page must not say
   * "not switched on" while this is true.
   */
  isLoading: boolean
  /** true when settings could not be read and no "on" answer is cached. */
  isError: boolean
  /** Re-read settings (for a "Try again" button). */
  retry: () => void
}

/**
 * The gate's decision, kept pure so it is testable without React.
 *
 * Why a refetch counts as "loading": the settings entry is shared app-wide
 * and cached with staleTime Infinity. Opening an admin page while logged out
 * fires GET /settings without a token (refused), and the refused/empty entry
 * used to be what the kitchen manager's page read right after login — so it
 * said "not switched on" until a reload. Login now refreshes the cache
 * (routes/login.tsx), and here a stale "off" that is being re-checked is
 * reported as loading instead of as an answer. A cached "on" is never held
 * back by a background refetch.
 */
export function resolveKitchenFlowGate(q: {
  flag: unknown
  status: 'pending' | 'error' | 'success'
  fetchStatus: 'fetching' | 'paused' | 'idle'
}): Omit<KitchenFlowGate, 'retry'> {
  const enabled = q.flag === true
  if (enabled) return { enabled: true, isLoading: false, isError: false }
  const settling = q.status === 'pending' || q.fetchStatus === 'fetching'
  return { enabled: false, isLoading: settling, isError: !settling && q.status === 'error' }
}

/** The same switch plus its loading state, for route pages that redirect when off. */
export function useKitchenFlowGate(): KitchenFlowGate {
  const { data, status, fetchStatus, refetch } = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
    staleTime: Infinity,
  })
  const all = data?.data as Record<string, unknown> | undefined
  return {
    ...resolveKitchenFlowGate({ flag: all?.kitchen_flow, status, fetchStatus }),
    retry: () => void refetch(),
  }
}

/**
 * The kitchen flow is switched on but its first-boot setup (the Kitchen
 * place, moving stock into Main Store) did not finish — the server's
 * `kitchen_flow_ready` is exactly false while `kitchen_flow` is true (pilot
 * fix, Batch 5 lane stock). Only an admin is told: they can restart the
 * backend or call support. A missing key (older backend) never warns.
 */
export function kitchenFlowSetupUnfinished(settings: Record<string, unknown> | undefined, role: string | null | undefined): boolean {
  if (!settings || role !== 'admin') return false
  return settings.kitchen_flow === true && settings.kitchen_flow_ready === false
}

/** The raw settings map from the shared cache (for po_approvers etc.). */
export function useAllSettings(): Record<string, unknown> | undefined {
  const { data } = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
    staleTime: Infinity,
    enabled: apiClient.isAuthenticated(),
    retry: false,
  })
  return data?.data as Record<string, unknown> | undefined
}
